import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Fab,
  Tab,
  Tabs,
  Skeleton,
  Container,
  Paper,
} from '@mui/material';
import ReservationModal from '../ReservationModal/ReservationModal';
import { useTheme } from '@mui/material/styles';
import { useDatePickerStyles } from '../DatePickerStyles';
import { useMediaQuery } from '@mui/material';
import { AuthContext } from '../../contexts/AuthContext';
import { fetchProfileData, sendReservations, fetchPlaceData, fetchTimeBlocks } from '../api';
import { useNotification } from '../NotificationContext';
import { useHookState } from '../NotificationApiHook';
import { PlaceContext } from '../../contexts/PlaceContext';
import { VersionContext } from '../../contexts/VersionContext';

const DatePicker = () => {
  const currentDate = new Date();

  let currentYear = currentDate.getFullYear();

  // Функция для генерации следующих трех месяцев
  const generateNextThreeMonths = () => {
    const months = [];
    let currentMonth = currentDate.getMonth();

    for (let i = 0; i < 3; i++) {
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear++;
      }
      const month = new Date(currentYear, currentMonth);
      months.push(month);
      currentMonth++;
    }
    return months;
  };

  const months = generateNextThreeMonths();

  // Функция для генерации всех дней выбранного месяца, начиная с текущей даты
  const generateDaysForMonth = (month, year) => {
    const firstDayOfMonth = new Date(year, month.getMonth(), 1);
    const lastDayOfMonth = new Date(year, month.getMonth() + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Обнуляем часы, минуты, секунды и миллисекунды иначе не вз
    const days = [];
    let currentDate = new Date(firstDayOfMonth);

    // Добавляем только дни, начиная с текущей даты
    while (currentDate <= lastDayOfMonth) {
      if (currentDate >= today) {
        days.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };

  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [openModal, setOpenModal] = useState(false);
  const { placeId } = useContext(PlaceContext);
  const theme = useTheme();
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);
  const [selectedMonthDays, setSelectedMonthDays] = useState(
    generateDaysForMonth(months[0], months[0].getFullYear()),
  );
  const isMobile = useMediaQuery('(max-width:600px)');
  const { openNotification } = useNotification();
  const { notificationState } = useHookState();
  const [loading, setLoading] = useState(false);
  const { styles, dayButtonClass } = useDatePickerStyles();
  const [profileData, setProfileData] = useState(null);
  const [placeData, setPlaceData] = useState(null);

  const fabVisible = selectedBlocks.length > 0;

  const { isLoggedIn, token } = useContext(AuthContext);

  const handleTabChange = (event, newMonth) => {
    setSelectedMonthIndex(newMonth);
    const daysForSelectedMonth = generateDaysForMonth(
      months[newMonth],
      months[newMonth].getFullYear(),
    );
    setSelectedMonthDays(daysForSelectedMonth);
    setSelectedDay(daysForSelectedMonth[0]);
    setSelectedBlocks([]);
  };

  useEffect(() => {
    if (token && placeId) {
      fetchProfileData(token, notificationState, 0, placeId)
        .then((data) => {
          setProfileData(data);
        })
        .catch((error) => {
          console.error('Ошибка при получении профиля:', error);
        });
    } else {
      setProfileData(null); // Обнуляем данные профиля, если токен отсутствует
    }
  }, [isLoggedIn]);

  // Функция для переключения выбранного временного блока
  const toggleTimeBlock = (block) => {
    setSelectedBlocks((prevBlocks) => {
      const blockIndex = prevBlocks.findIndex(
        (selectedBlock) => selectedBlock.start === block.start,
      );

      if (blockIndex !== -1) {
        // Если блок уже выбран, снимаем его выбор
        return prevBlocks.filter((selectedBlock) => selectedBlock.start !== block.start);
      } else {
        // Если блок еще не выбран, добавляем его в список выбранных
        return [...prevBlocks, block];
      }
    });
  };

  // Функция для обработки выбора дня
  const handleDaySelect = async (day) => {
    setSelectedDay(day);
    setSelectedBlocks([]); // Сбрасываем выбранные блоки при выборе нового дня
  };

  const fetchTimeRanges = async () => {
    try {
      setLoading(true);
      const timeBlocksFromServer = await fetchTimeBlocks(selectedDay, placeId, notificationState);
      // Обработка полученных блоков и обновление состояния
      setTimeBlocks(timeBlocksFromServer);
    } catch (error) {
      openNotification(`Произошла ошибка получения данных с сервера. Ошибка ${error}`, 'error');
      console.error('Error fetching time blocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  const days = [];
  for (let i = 0; i < 7; i++) {
    const currentDay = new Date(today);
    currentDay.setDate(today.getDate() + i);
    days.push(currentDay);
  }

  const handleReserveClick = () => {
    setOpenModal(true);
  };

  // Получаем выбранный месяц
  const selectedMonth = months[selectedMonthIndex];

  const resolveTransaction = async (status) => {
    let updatedTimeBlocks = [];
    try {
      if (status.total_price === 0) {
        openNotification(`Ваша бронь подтверждена и оплачена с баланса вашего профиля`, 'success');
      } else {
        setLoading(true);
        const isSuccess = await sendReservations(token, status, notificationState);
        if (!isSuccess) {
          console.log('Failed to send reservations.');
        }
      }
    } finally {
      updatedTimeBlocks = await fetchTimeBlocks(selectedDay, placeId);
      setTimeBlocks(updatedTimeBlocks);
      setSelectedBlocks([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('selectedDay', selectedDay);
    if (placeId) {
      fetchTimeRanges();
      fetchPlaceData(notificationState, placeId).then((data) => {
        setPlaceData(data[0]);
      });
    }
  }, [selectedDay, placeId]);

  const getProfileBalance = () => {
    if (profileData) {
      return profileData.balance;
    }
  };

  const getProfileData = () => {
    if (profileData) {
      return profileData;
    }
  };

  const getPlaceData = () => {
    if (placeData) {
      return placeData;
    }
  };

  const calculateTotalPrice = () => {
    // Проверяем, есть ли выбранные блоки
    if (selectedBlocks.length === 0) {
      return 0;
    }

    // Суммируем цены выбранных блоков
    const totalPrice = selectedBlocks.reduce((acc, block) => acc + Number(block.price), 0);
    return totalPrice;
  };

  const calculateTotalTime = () => {
    const totalMinutes = selectedBlocks.reduce((total, block) => {
      const startTime = block.start;
      const endTime = block.end;
      const diffInMinutes = (endTime - startTime) / (1000 * 60);
      return total + diffInMinutes;
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60); // Округляем до ближайшей минуты

    if (totalMinutes <= 30) {
      return '30 минут';
    } else {
      return `${hours > 0 ? `${hours} час${hours === 1 ? '' : hours >= 5 ? 'ов' : 'a'}` : ''} ${
        minutes > 0 ? `${minutes} минут${minutes === 1 ? '' : minutes >= 5 ? 'ы' : ''}` : ''
      }`;
    }
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tabs
          value={selectedMonthIndex}
          onChange={handleTabChange}
          centered
        >
          {months.map((month, index) => (
            <Tab
              key={index}
              label={month.toLocaleDateString('ru-RU', { month: 'long' })}
            />
          ))}
        </Tabs>
      </Box>
      <Box
        key={selectedDay}
        mb={2}
        sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', ...styles.scrollbar }}
      >
        {selectedMonthDays.map((day) => (
          <Button
            key={day.toISOString()}
            variant='outlined'
            onClick={() => handleDaySelect(day)}
            sx={dayButtonClass}
          >
            <Typography
              variant='body2'
              className='day'
            >
              {day.toLocaleDateString('ru-RU', { weekday: 'short' })}
            </Typography>
            <Typography
              variant='body1'
              className={`date ${
                selectedDay && selectedDay.toDateString() === day.toDateString()
                  ? 'pressed'
                  : 'default'
              }`}
              data-locator='working_day_number'
            >
              {day.getDate()}
            </Typography>
          </Button>
        ))}
      </Box>
      {loading ? (
        <Container sx={{ marginTop: '100px' }}>
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </Container>
      ) : (
        selectedDay && (
          <Box
            sx={{
              maxHeight: fabVisible ? 'calc(100svh - 350px)' : 'calc(100svh - 300px)',
              minHeight: fabVisible ? 'calc(100svh - 350px)' : 'calc(100svh - 300px)',
              overflow: 'auto',
              ...styles.scrollbar,
            }}
          >
            <Typography
              variant='h6'
              gutterBottom
              sx={{ justifyContent: 'center', textAlign: 'center', fontSize: '1.15rem' }}
            >
              Бронь на{' '}
              {selectedDay.toLocaleDateString('ru-RU', {
                month: 'long',
                day: 'numeric',
              })}{' '}
              {selectedDay.toLocaleDateString('ru-RU', {
                weekday: 'long',
              })}
            </Typography>
            <List>
              {timeBlocks.map((block, index) => {
                let date = new Date();
                const isBlockSelectable = block.end > date;
                const isLastBlock = index === timeBlocks.length - 1;
                const isLastBlockSelectable = block.end > date && isLastBlock;
                return (
                  <React.Fragment key={block.id}>
                    {!isLastBlockSelectable && isLastBlock && (
                      <Box sx={{ width: '90%', margin: '0 auto', mt: 10 }}>
                        <Paper
                          elevation={3}
                          sx={{ p: 2 }}
                        >
                          <Typography
                            variant='h6'
                            sx={{ textAlign: 'center' }}
                          >
                            Зал уже закрыт
                          </Typography>
                          <Typography
                            variant='body1'
                            sx={{ textAlign: 'center' }}
                          >
                            К сожалению, на сегодня нет доступного времени для бронирования.
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                    {isBlockSelectable && (
                      <ListItem
                        key={block.id}
                        button
                        onClick={() => toggleTimeBlock(block)}
                        selected={selectedBlocks.some(
                          (selectedBlock) => selectedBlock.start === block.start,
                        )}
                        disabled={block.disabled}
                        sx={{
                          color: '#000000b0',
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '& .MuiListItemText-secondary': {
                              color: theme.palette.primary.contrastText,
                            },
                          },
                          '&:hover': {
                            backgroundColor: '#5f51a666',
                          },
                          '&.Mui-selected:hover': {
                            backgroundColor: theme.palette.primary.light,
                          },
                        }}
                      >
                        <ListItemText
                          primary={`${block.start.getHours()}:${block.start
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')} - ${block.end.getHours()}:${block.end
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}`}
                          secondary={
                            block.availableTableNumbers
                              ? `Доступные столы | ${block.availableTableNumbers} |`
                              : 'Нет доступных столов'
                          }
                        />
                        <Typography variant='body2'> {block.price} ₽ </Typography>
                      </ListItem>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )
      )}
      <Fab
        color='primary'
        onClick={handleReserveClick}
        disabled={!isLoggedIn}
        sx={{
          backgroundColor: theme.palette.primary.main,
          position: 'sticky',
          bottom: '58px',
          marginLeft: isMobile ? '0' : 'calc(50% - 275px)',
          display: fabVisible ? 'block' : 'none',
          width: '100%',
          maxWidth: '600px',
          height: '80px',
          lineHeight: '30px',
          padding: '15px',
          zIndex: '98',
          borderRadius: '0',
          '&.Mui-disabled': {
            backgroundColor: '#4b4b4b',
            color: theme.palette.primary.contrastText,
          },
        }}
      >
        {isLoggedIn ? (
          <Box>
            Забронировать
            <Box>
              {calculateTotalTime()} / {calculateTotalPrice()} ₽
            </Box>
          </Box>
        ) : (
          <Box>Войдите, чтобы забронировать</Box>
        )}
      </Fab>
      <ReservationModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
        }}
        selectedBlocks={selectedBlocks}
        day={selectedDay}
        balance={getProfileBalance()}
        profileData={getProfileData()}
        placeData={getPlaceData()}
        onReserve={(status) => {
          console.log('Итоговый JSON:', JSON.stringify(status, null, 2));
          resolveTransaction(status);
        }}
        onReserveClick={handleReserveClick}
      />
    </Box>
  );
};

export default DatePicker;
